import swal from "sweetalert";
import {
  loginConfirmedAction,
  logout,
} from '../store/actions/AuthActions';

import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  unlink,
  updatePassword,
  reauthenticateWithCredential,
  AuthErrorCodes
} from "firebase/auth";
import { initializeApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: "AIzaSyC5Gyjy4-2QsicCUu99behsspUgtOXTdzc",
  authDomain: "ledgerloop.firebaseapp.com"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Enhanced error handling with security considerations
const RATE_LIMIT_ERRORS = new Set([
  'auth/too-many-requests',
  'auth/quota-exceeded'
]);

const SECURITY_SENSITIVE_ERRORS = new Set([
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/requires-recent-login'
]);

// Utility function to sanitize email for logging (security)
function sanitizeEmailForLogging(email) {
  if (!email || typeof email !== 'string') return '[invalid-email]';
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return '[malformed-email]';
  return `${localPart.substring(0, 2)}***@${domain}`;
}

// Enhanced sign up with account linking detection
export async function signUp(email, password) {
  try {
    // First, check if user already exists with different sign-in methods
    const existingMethods = await checkUserSignInMethods(email);

    if (existingMethods.methods.length > 0) {
      // Account exists - guide user to appropriate sign-in method
      if (existingMethods.hasGoogle && !existingMethods.hasPassword) {
        throw {
          code: 'auth/account-exists-different-credential',
          message: 'Ett konto med denna e-postadress finns redan. Logga in med Google istället.',
          existingMethods: existingMethods.methods
        };
      }

      if (existingMethods.hasPassword) {
        throw {
          code: 'auth/email-already-in-use',
          message: 'Ett konto med denna e-postadress finns redan. Logga in istället.',
          existingMethods: existingMethods.methods
        };
      }
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log("Signup successful for:", sanitizeEmailForLogging(email));
    return userCredential;
  } catch (error) {
    console.error("Signup error for", sanitizeEmailForLogging(email), ":", error.code);
    throw error;
  }
}

// Enhanced login with better error handling
export async function login(email, password) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log("Login successful for:", sanitizeEmailForLogging(email));
    return userCredential;
  } catch (error) {
    console.error("Login error for", sanitizeEmailForLogging(email), ":", error.code);

    // Enhanced error handling for account linking scenarios
    if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
      try {
        const signInMethods = await checkUserSignInMethods(email);
        if (signInMethods.hasGoogle && !signInMethods.hasPassword) {
          throw {
            ...error,
            code: 'auth/google-only-user',
            message: 'Detta konto använder Google för inloggning. Klicka på "Fortsätt med Google".',
            existingMethods: signInMethods.methods
          };
        }
      } catch (methodsError) {
        // If we can't check methods, fall back to original error
        console.warn("Could not check sign-in methods:", methodsError);
      }
    }

    throw error;
  }
}

// Enhanced function to check available sign-in methods
export async function checkUserSignInMethods(email) {
  try {
    // Input validation
    if (!email || typeof email !== 'string' || !email.includes('@')) {
      throw new Error('Invalid email address');
    }

    const methods = await fetchSignInMethodsForEmail(auth, email);

    return {
      hasPassword: methods.includes('password'),
      hasGoogle: methods.includes('google.com'),
      hasFacebook: methods.includes('facebook.com'),
      methods: methods,
      isEmpty: methods.length === 0
    };
  } catch (error) {
    console.error("Error checking sign-in methods for", sanitizeEmailForLogging(email), ":", error.code);

    // Don't expose internal errors
    if (error.code === 'auth/invalid-email') {
      throw error;
    }

    // For other errors, return safe defaults
    return {
      hasPassword: false,
      hasGoogle: false,
      hasFacebook: false,
      methods: [],
      isEmpty: true,
      error: true
    };
  }
}

// Enhanced password reset with smart detection
export async function sendPasswordReset(email) {
  try {
    // Input validation
    if (!email || typeof email !== 'string') {
      throw { code: 'auth/invalid-email', message: 'Ogiltig e-postadress' };
    }

    const signInMethods = await checkUserSignInMethods(email);

    // Handle different account scenarios
    if (signInMethods.isEmpty && !signInMethods.error) {
      throw {
        code: 'auth/user-not-found',
        message: 'Ingen användare hittades med den e-postadressen'
      };
    }

    if (signInMethods.hasGoogle && !signInMethods.hasPassword) {
      throw {
        code: 'auth/google-only-user',
        message: 'Detta konto använder Google för inloggning. Använd "Fortsätt med Google" istället.',
        canAddPassword: true
      };
    }

    if (!signInMethods.hasPassword && signInMethods.methods.length > 0) {
      throw {
        code: 'auth/no-password-method',
        message: 'Detta konto har inget lösenord. Kontakta support för hjälp.',
        availableMethods: signInMethods.methods
      };
    }

    // Send password reset email with security settings
    await sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/login`,
      handleCodeInApp: false,
    });

    console.log("Password reset sent for:", sanitizeEmailForLogging(email));
    return { success: true };

  } catch (error) {
    console.error("Password reset error for", sanitizeEmailForLogging(email || 'unknown'), ":", error.code);
    throw error;
  }
}

// Function to allow users to add password to Google-only accounts
export async function addPasswordToCurrentUser(newPassword) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw { code: 'auth/no-current-user', message: 'Ingen användare är inloggad' };
    }

    // Validate password strength
    if (!newPassword || newPassword.length < 6) {
      throw { code: 'auth/weak-password', message: 'Lösenordet måste vara minst 6 tecken' };
    }

    // Create email credential
    const credential = EmailAuthProvider.credential(user.email, newPassword);

    // Link the credential to the current user
    await linkWithCredential(user, credential);

    console.log("Password added for user:", sanitizeEmailForLogging(user.email));
    return { success: true, message: 'Lösenord har lagts till på ditt konto' };

  } catch (error) {
    console.error("Error adding password:", error.code);

    if (error.code === 'auth/provider-already-linked') {
      throw {
        code: error.code,
        message: 'Detta konto har redan ett lösenord'
      };
    }

    if (error.code === 'auth/credential-already-in-use') {
      throw {
        code: error.code,
        message: 'Denna e-postadress används redan med ett lösenord'
      };
    }

    throw error;
  }
}

// Enhanced function to unlink providers (for account management)
export async function unlinkProvider(providerId) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw { code: 'auth/no-current-user', message: 'Ingen användare är inloggad' };
    }

    // Security check - don't allow unlinking if it's the only sign-in method
    const signInMethods = await checkUserSignInMethods(user.email);
    if (signInMethods.methods.length <= 1) {
      throw {
        code: 'auth/cannot-unlink-last-provider',
        message: 'Du kan inte ta bort den enda inloggningsmetoden'
      };
    }

    await unlink(user, providerId);
    console.log("Provider unlinked for user:", sanitizeEmailForLogging(user.email));
    return { success: true };

  } catch (error) {
    console.error("Error unlinking provider:", error.code);
    throw error;
  }
}

// Verify reset code with enhanced security
export async function verifyResetCode(code) {
  try {
    // Input validation
    if (!code || typeof code !== 'string' || code.length < 10) {
      throw { code: 'auth/invalid-action-code', message: 'Ogiltig återställningskod' };
    }

    const email = await verifyPasswordResetCode(auth, code);
    return { success: true, email };
  } catch (error) {
    console.error("Verify reset code error:", error.code);
    throw error;
  }
}

// Confirm password reset with enhanced validation
export async function confirmPasswordResetWithCode(code, newPassword) {
  try {
    // Input validation
    if (!code || typeof code !== 'string') {
      throw { code: 'auth/invalid-action-code', message: 'Ogiltig återställningskod' };
    }

    if (!newPassword || newPassword.length < 8) {
      throw { code: 'auth/weak-password', message: 'Lösenordet måste vara minst 8 tecken' };
    }

    // Additional password strength validation
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      throw {
        code: 'auth/weak-password',
        message: 'Lösenordet måste innehålla stora och små bokstäver samt siffror'
      };
    }

    await confirmPasswordReset(auth, code, newPassword);
    console.log("Password reset confirmed successfully");
    return { success: true };
  } catch (error) {
    console.error("Confirm password reset error:", error.code);
    throw error;
  }
}

// Enhanced error formatting with security considerations
export function formatError(firebaseError) {
  // Don't log sensitive information
  const errorCode = firebaseError?.code || 'unknown';
  console.warn("Auth error occurred:", errorCode);

  switch (errorCode) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      swal("Fel vid inloggning", "E-post eller lösenord är felaktigt. Om du registrerade dig med Google, prova 'Fortsätt med Google'.", "error");
      break;

    case 'auth/google-only-user':
      swal({
        title: "Använd Google för inloggning",
        text: "Detta konto använder Google för inloggning. Du kan också lägga till ett lösenord i kontoinställningar.",
        icon: "info",
        buttons: {
          cancel: "OK",
          confirm: {
            text: "Fortsätt med Google",
            value: "google"
          }
        }
      }).then((value) => {
        if (value === "google") {
          // Trigger Google sign-in
          window.dispatchEvent(new CustomEvent('triggerGoogleSignIn'));
        }
      });
      break;

    case 'auth/account-exists-different-credential':
      swal("Konto finns redan", "Ett konto med denna e-postadress finns redan med en annan inloggningsmetod. Försök logga in istället.", "warning");
      break;

    case 'auth/too-many-requests':
      swal("För många försök", "För många inloggningsförsök från denna enhet. Vänta en stund och försök igen.", "error");
      break;

    case 'auth/user-not-found':
      swal("Ingen användare", "Ingen användare hittades med den e-postadressen.", "error");
      break;

    case 'auth/invalid-email':
      swal("Ogiltig e-post", "E-postadressen är inte giltig.", "error");
      break;

    case 'auth/weak-password':
      swal("Svagt lösenord", firebaseError.message || "Lösenordet är för svagt. Använd minst 8 tecken med stora och små bokstäver samt siffror.", "error");
      break;

    case 'auth/email-already-in-use':
      swal("E-post används redan", "E-postadressen används redan. Försök logga in istället.", "error");
      break;

    case 'auth/invalid-action-code':
      swal("Ogiltig länk", "Återställningslänken är ogiltig eller felaktig.", "error");
      break;

    case 'auth/expired-action-code':
      swal("Utgången länk", "Återställningslänken har gått ut. Begär en ny.", "error");
      break;

    case 'auth/user-disabled':
      swal("Konto inaktiverat", "Detta konto har inaktiverats. Kontakta support.", "error");
      break;

    case 'auth/operation-not-allowed':
      swal("Operation inte tillåten", "Denna inloggningsmetod är inte aktiverad.", "error");
      break;

    case 'auth/requires-recent-login':
      swal("Säkerhetsverifiering krävs", "Du måste logga in igen för att utföra denna åtgärd.", "warning");
      break;

    default:
      // Generic error for unknown cases - don't expose internal details
      swal("Ett fel inträffade", "Ett oväntat fel inträffade. Försök igen senare.", "error");
      return '';
  }
}

// Existing functions remain the same...
export function saveTokenInLocalStorage(tokenDetails) {
  console.log("saveTokenInLocalStorage: ", tokenDetails);

  tokenDetails.expireDate = new Date(
    new Date().getTime() + tokenDetails._tokenResponse.expiresIn * 1000,
  );
  localStorage.setItem('userDetails', JSON.stringify(tokenDetails));
}

export function runLogoutTimer(dispatch, timer, history) {
  setTimeout(() => {
    dispatch(logout(history));
  }, timer);
}

export function checkAutoLogin(dispatch, history) {
  const tokenDetailsString = localStorage.getItem('userDetails');
  let tokenDetails = '';
  if (!tokenDetailsString) {
    dispatch(logout(history));
    return;
  }

  try {
    tokenDetails = JSON.parse(tokenDetailsString);
  } catch (error) {
    console.error("Invalid token data in localStorage:", error);
    dispatch(logout(history));
    return;
  }

  let expireDate = new Date(tokenDetails.expireDate);
  let todaysDate = new Date();

  if (todaysDate > expireDate) {
    dispatch(logout(history));
    return;
  }

  const selectedCompanyString = localStorage.getItem('selectedCompany');
  let selectedCompany = {
    companyName: '',
    companyId: '',
    companyRegNo: '',
  };
  if (selectedCompanyString) {
    try {
      selectedCompany = JSON.parse(selectedCompanyString);
    } catch (error) {
      console.warn("Invalid selectedCompany data in localStorage:", error);
      // Continue with default selectedCompany
    }
  }

  dispatch(loginConfirmedAction(tokenDetails, selectedCompany));

  const timer = expireDate.getTime() - todaysDate.getTime();
  runLogoutTimer(dispatch, timer, history);
}

